/**
 * Вложения задачи.
 *
 * Файл едет в files-service напрямую, минуя трекер, а трекеру достаётся только
 * ключ файла: прокачивать байты через трекер значило бы держать вторую копию
 * логики загрузки — так это и задумано на стороне сервиса.
 *
 * Бакет приватный: ссылку на вложение трекер выписывает при чтении списка и
 * живёт она час. Публичный бакет здесь не годится — в нём лежит статика сайта,
 * а ссылка на неё вечная и ничем не прикрыта.
 */
import { issueApi } from '@/api'
import { http } from '@/api/http'
import { intlLocale } from '@/features/issues/format'

import type { V1Attachment } from '@/api/clients/tracker-issue'

export interface Attachment {
  id: string
  fileName: string
  mimeType: string
  sizeBytes: number
  uploadedBy: string
  createdAt: string
  /** Временная ссылка на скачивание — её выдаёт сервис при чтении списка */
  url: string
}

const toAttachment = (attachment: V1Attachment): Attachment => ({
  id: attachment.id ?? '',
  fileName: attachment.fileName ?? '',
  mimeType: attachment.mimeType ?? '',
  sizeBytes: Number(attachment.sizeBytes ?? 0),
  uploadedBy: attachment.uploadedBy ?? '',
  createdAt: attachment.createdAt ?? '',
  url: attachment.url ?? '',
})

export const listAttachments = async (issueId: string): Promise<Attachment[]> => {
  const { data } = await issueApi.issueServiceListAttachments(issueId)

  return (data.attachments ?? []).map(toAttachment)
}

interface UploadResponse {
  /** Ключ файла в приватном бакете: `issue/<id>/<имя>` */
  name?: string
}

/**
 * Кладёт файл в приватное хранилище и отдаёт его ключ.
 *
 * Задача здесь — та самая сущность, к которой файл прикрепляют: сервис
 * раскладывает приватные файлы по папкам владельцев, и без пары «тип и
 * идентификатор» ему некуда положить (DEV-1505).
 */
export const uploadIssueFile = async (issueId: string, file: File): Promise<string> => {
  const form = new FormData()

  form.append('file', file)
  form.append('entity_type', 'issue')
  form.append('entity_id', issueId)

  const { data } = await http.post<UploadResponse>('/api/v1/files/upload-private', form, {
    // У клиента по умолчанию стоит application/json, а с ним axios сериализует
    // форму в JSON и файл до сервиса не доедет. Границу multipart дальше
    // проставит браузер.
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  if (!data.name) throw new Error('files-service вернул ответ без имени файла')

  return data.name
}

/** Привязывает уже загруженный файл к задаче */
export const createAttachment = async (issueId: string, fileId: string, file: File) => {
  const { data } = await issueApi.issueServiceCreateAttachment(issueId, {
    fileId,
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    sizeBytes: String(file.size),
  })

  return data.attachment ? toAttachment(data.attachment) : null
}

export const deleteAttachment = async (id: string): Promise<void> => {
  await issueApi.issueServiceDeleteAttachment(id)
}

/** Тип файла в плитке — расширение имени, а не mime: «PNG», «MD», «XLSX» */
export const fileKind = (fileName: string, mimeType: string): string => {
  const extension = fileName.includes('.') ? fileName.split('.').pop() ?? '' : ''

  if (extension) return extension.toUpperCase()

  // Имя без расширения бывает у файлов из мессенджеров: тогда подтип mime —
  // единственное, что вообще что-то говорит о файле.
  return (mimeType.split('/').pop() ?? '').toUpperCase()
}

/**
 * Размер файла — «248 КБ», «1,4 МБ».
 *
 * Байты не показываем: в макете их нет, и «4096 Б» ничего не добавляет к
 * «4 КБ». Единицы приходят снаружи — формат живёт вне компонента, а переводы
 * лежат в словаре.
 */
export const formatFileSize = (
  bytes: number,
  units: { kb: string; mb: string },
  locale: string,
): string => {
  const kb = bytes / 1024

  if (kb < 1024) {
    // Меньше килобайта — всё равно «1 КБ»: ноль рядом с именем файла читается
    // как «файл не загрузился».
    return `${Math.max(1, Math.round(kb))} ${units.kb}`
  }

  return `${new Intl.NumberFormat(intlLocale(locale), { maximumFractionDigits: 1 }).format(kb / 1024)} ${units.mb}`
}
